// Lightweight, privacy-conscious first-party website analytics tracker.
// - Async, non-blocking: events are queued and flushed on idle / interval / pagehide.
// - Consent-gated: nothing non-essential is sent until analytics consent is granted.
// - Anonymous: a first-party visitor id + rolling session id (30-min inactivity).
// - Never sends form-field contents, passwords, or PII.

import { analyticsAllowed, onConsentChange } from "@/lib/consent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VID_KEY = "arq_a_vid";
const SESSION_KEY = "arq_a_session";
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

let queue = [];
let context = null;
let flushTimer = null;
let started = false;
let pendingIsNewVisitor = false;
let identifiedEmail = null;

export function identify(email) {
  if (email) identifiedEmail = String(email).toLowerCase().slice(0, 160);
}

function uuid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getVisitorId() {
  try {
    let id = localStorage.getItem(VID_KEY);
    if (!id) {
      id = uuid();
      localStorage.setItem(VID_KEY, id);
      pendingIsNewVisitor = true;
    }
    return id;
  } catch (_e) {
    return uuid();
  }
}

function detectDevice(ua) {
  if (/ipad|tablet|(android(?!.*mobile))|kindle|silk|playbook/i.test(ua)) return "tablet";
  if (/mobi|iphone|ipod|android.*mobile|windows phone/i.test(ua)) return "mobile";
  return "desktop";
}

function detectBrowser(ua) {
  if (/edg\//i.test(ua)) return "Edge";
  if (/opr\/|opera/i.test(ua)) return "Opera";
  if (/chrome\//i.test(ua) && !/edg\//i.test(ua)) return "Chrome";
  if (/firefox\//i.test(ua)) return "Firefox";
  if (/safari\//i.test(ua) && !/chrome/i.test(ua)) return "Safari";
  return "Other";
}

function detectOS(ua) {
  if (/windows/i.test(ua)) return "Windows";
  if (/mac os x|macintosh/i.test(ua)) return "macOS";
  if (/android/i.test(ua)) return "Android";
  if (/iphone|ipad|ipod/i.test(ua)) return "iOS";
  if (/linux/i.test(ua)) return "Linux";
  return "Other";
}

function classifySource(referrer, utm) {
  const medium = (utm.utm_medium || "").toLowerCase();
  const source = (utm.utm_source || "").toLowerCase();
  if (/(cpc|ppc|paid|display|paidsocial|paid_social)/.test(medium)) return "paid";
  if (source) {
    if (/instagram|ig/.test(source)) return "instagram";
    if (/facebook|fb|meta/.test(source)) return "facebook";
    if (/linkedin/.test(source)) return "linkedin";
    if (/google|bing|search/.test(source)) return "search";
    if (/email|newsletter|mailchimp|postmark/.test(source) || medium === "email") return "email";
    return "referral";
  }
  if (!referrer) return "direct";
  try {
    const host = new URL(referrer).hostname.replace(/^www\./, "");
    if (host.includes(window.location.hostname)) return "direct";
    if (/google\.|bing\.|duckduckgo|yahoo\.|ecosia/.test(host)) return "search";
    if (/instagram\.com/.test(host)) return "instagram";
    if (/facebook\.com|fb\.com|l\.facebook/.test(host)) return "facebook";
    if (/linkedin\.com|lnkd\.in/.test(host)) return "linkedin";
    if (/mail\.|outlook|gmail/.test(host)) return "email";
    return "referral";
  } catch (_e) {
    return "other";
  }
}

function readUTM() {
  const p = new URLSearchParams(window.location.search);
  const utm = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => {
    const v = p.get(k);
    if (v) utm[k] = v.slice(0, 120);
  });
  return utm;
}

function initSession() {
  const now = Date.now();
  let sess = null;
  try { sess = JSON.parse(sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY) || "null"); } catch (_e) { sess = null; }
  const utm = readUTM();
  const isFresh = !sess || (now - (sess.last || 0)) > SESSION_TIMEOUT;

  if (isFresh) {
    const source_category = classifySource(document.referrer || "", utm);
    sess = {
      id: uuid(),
      started: now,
      last: now,
      source_category,
      campaign: utm.utm_campaign || null,
      landing_page: window.location.pathname,
      referrer: document.referrer || "",
      utm,
      isNew: true,
    };
  } else {
    sess.last = now;
    sess.isNew = false;
  }
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(sess));
    localStorage.setItem(SESSION_KEY, JSON.stringify(sess));
  } catch (_e) { /* ignore */ }
  return { sess, isFresh };
}

function touchSession() {
  try {
    const sess = JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
    if (sess) {
      sess.last = Date.now();
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(sess));
      localStorage.setItem(SESSION_KEY, JSON.stringify(sess));
    }
  } catch (_e) { /* ignore */ }
}

function buildContext() {
  const ua = navigator.userAgent || "";
  const { sess } = initSession();
  return {
    visitor_id: getVisitorId(),
    session: sess,
    device: detectDevice(ua),
    browser: detectBrowser(ua),
    os: detectOS(ua),
    screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
  };
}

function enrich(event, props) {
  const s = context.session;
  return {
    event,
    event_id: uuid(),
    page_url: window.location.href.slice(0, 500),
    page_path: window.location.pathname,
    page_title: document.title,
    referrer: s.referrer,
    source_category: s.source_category,
    campaign: s.campaign,
    landing_page: s.landing_page,
    utm_source: s.utm.utm_source || null,
    utm_medium: s.utm.utm_medium || null,
    utm_campaign: s.utm.utm_campaign || null,
    utm_content: s.utm.utm_content || null,
    utm_term: s.utm.utm_term || null,
    device: context.device,
    browser: context.browser,
    os: context.os,
    screen: context.screen,
    ts: new Date().toISOString(),
    props: props || {},
  };
}

function isAuthed() {
  try { return !!localStorage.getItem("arq_token"); } catch (_e) { return false; }
}

function scheduleFlush() {
  if (flushTimer) return;
  const run = () => { flushTimer = null; flush(false); };
  flushTimer = setTimeout(() => {
    if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 2000 });
    else run();
  }, 1500);
}

async function flush(useBeacon) {
  if (!queue.length || !analyticsAllowed()) return;
  const batch = queue.splice(0, queue.length);
  const body = JSON.stringify({
    visitor_id: context.visitor_id,
    session_id: context.session.id,
    events: batch,
    consent: { analytics: true },
    authed: isAuthed(),
    is_new_visitor: pendingIsNewVisitor,
    email: identifiedEmail,
  });
  pendingIsNewVisitor = false;
  const url = `${API}/analytics/collect`;
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      return;
    }
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    });
    if (!res.ok && res.status !== 429) queue.unshift(...batch); // safe retry (non-rate-limit)
  } catch (_e) {
    queue.unshift(...batch); // network failure — retry next flush
  }
}

export function track(event, props) {
  if (!analyticsAllowed()) return;
  if (!context) context = buildContext();
  touchSession();
  queue.push(enrich(event, props));
  scheduleFlush();
}

let lastPath = null;
let pageEnterTs = Date.now();

export function trackPageView() {
  if (!analyticsAllowed()) return;
  if (!context) context = buildContext();
  const path = window.location.pathname;
  // Emit dwell for the previous page.
  if (lastPath && lastPath !== path) {
    track("page_exit", { duration_ms: Date.now() - pageEnterTs });
  }
  const { sess } = initSession();
  if (sess.isNew) {
    track(pendingIsNewVisitor ? "first_visit" : "returning_visit");
    track("session_start");
  }
  track("page_view");
  if (path === "/pricing") track("pricing_visit");
  lastPath = path;
  pageEnterTs = Date.now();
}

export function trackConversion(name, props) {
  track(name, props);
  flush(false);
}

// Global click delegation: phone / email / external links.
function onGlobalClick(e) {
  const a = e.target.closest?.("a, [data-analytics-event]");
  if (!a) return;
  const custom = a.getAttribute?.("data-analytics-event");
  if (custom) {
    track(custom, {
      cta: a.getAttribute("data-analytics-cta") || undefined,
      label: (a.getAttribute("data-analytics-label") || a.innerText || "").slice(0, 80),
    });
    return;
  }
  const href = a.getAttribute?.("href") || "";
  if (href.startsWith("tel:")) { track("phone_click", { label: href.slice(4, 40) }); return; }
  if (href.startsWith("mailto:")) { track("email_click"); return; }
  if (/^https?:\/\//i.test(href)) {
    try {
      const host = new URL(href).hostname;
      if (!host.includes(window.location.hostname)) {
        track("external_link_click", { link_host: host });
      }
    } catch (_e) { /* ignore */ }
  }
}

export function startTracker() {
  if (started) return;
  started = true;
  context = buildContext();
  document.addEventListener("click", onGlobalClick, true);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      track("page_exit", { duration_ms: Date.now() - pageEnterTs });
      flush(true);
    }
  });
  window.addEventListener("pagehide", () => { flush(true); });
  // Expose for imperative conversion tracking from components.
  window.arqAnalytics = { track, trackConversion, trackPageView, identify };
  onConsentChange((c) => { if (c.analytics) { context = buildContext(); trackPageView(); } });
}
